import { useApiClient } from "@procurement-agents/auth";
import { scopeApi, type ScopeApi } from "@procurement-agents/scope-api";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface Toast { id: number; text: string; tone: "info" | "warn" }
const ApiContext = createContext<{ api: ScopeApi; toast: (text: string, tone?: Toast["tone"]) => void } | null>(null);

export function ScopeApiProvider({ children, api: override }: { children: ReactNode; api?: ScopeApi }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toast = useCallback((text: string, tone: Toast["tone"] = "info") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);
  const onStatus = useCallback((status: number, detail: string) => {
    if (status === 403) toast(`Not permitted: ${detail}`, "warn");
    if (status === 409) toast(detail, "warn");
  }, [toast]);
  const fetcher = useApiClient(onStatus);
  const api = useMemo(() => override ?? scopeApi(fetcher), [fetcher, override]);
  return (
    <ApiContext.Provider value={{ api, toast }}>
      {children}
      <div className="toasts" aria-live="polite">{toasts.map((t) => <div key={t.id} className={`toast ${t.tone}`}>{t.text}</div>)}</div>
    </ApiContext.Provider>
  );
}

/** Provides a ready-made client without the auth context (tests, storybook-style harnesses). */
export function StaticScopeApiProvider({ api, children, toast = () => {} }: { api: ScopeApi; children: ReactNode; toast?: (text: string, tone?: Toast["tone"]) => void }) {
  const value = useMemo(() => ({ api, toast }), [api, toast]);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

/** The client when there is one; renderers that also work without it (voice) use this. */
export function useOptionalScopeApi() {
  return useContext(ApiContext);
}

export function useScopeApi() {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error("useScopeApi outside ScopeApiProvider");
  return ctx;
}
